/**
 * Choosing which club you are looking at.
 *
 * Rendered only when there is a choice to make: a single-club user has no
 * switcher, because a control with one option is a control that teaches
 * nothing and takes a click to discover.
 *
 * The switch is a SERVER act — /auth/switch-organization mints a new token
 * with the new organization claim and sets the cookie. The page then reloads
 * rather than updating state in place: the session claim changed, so every
 * cached answer on screen belongs to the previous club, and re-fetching them
 * one by one is how a stale panel from another club ends up on the page.
 */
import { Check, ChevronsUpDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { trackEvent } from '../../lib/analytics'
import { api, type Session } from '../../lib/api'
import { pathAfterOrgSwitch } from './nav'

type Org = Session['organizations'][number]

export default function OrgSwitcher({ session }: { session: Session }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)
  // When the rail is collapsed the menu flies out to the right — but the
  // sidebar clips its overflow, so an absolutely-positioned flyout is
  // invisible. Pin it with `position: fixed` (escapes ancestor overflow)
  // anchored to the button's on-screen box. Null = expanded, use the CSS.
  const [fixedPos, setFixedPos] = useState<{ left: number; top: number } | null>(null)

  const toggle = () => {
    setOpen((v) => {
      const next = !v
      if (next && box.current?.closest('.app-sidebar')?.classList.contains('is-collapsed')) {
        const r = box.current.getBoundingClientRect()
        setFixedPos({ left: r.right + 6, top: r.top })
      } else {
        setFixedPos(null)
      }
      return next
    })
  }

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const orgs = session.organizations
  if (orgs.length < 2) return null

  async function choose(org: Org) {
    if (org.id === session.org?.id) { setOpen(false); return }
    setBusy(true)
    setError(null)
    try {
      await api('/api/v1/auth/switch-organization', {
        method: 'POST',
        body: JSON.stringify({ organization_id: org.id }),
      })
      // Reaches Mixpanel despite the reload below: the SDK's batch queue
      // persists in localStorage and ships on the next load.
      trackEvent('Organization Switched', {
        organization_id: org.id,
        organization_name: org.name,
        from_organization_id: session.org?.id ?? null,
      })
      // A full load, not a router navigation: the switch mints a new token
      // server-side, and every cached page and query in memory belongs to the
      // club we just left.
      const next = pathAfterOrgSwitch(window.location.pathname)
      if (next) window.location.assign(next)
      else window.location.reload()
    } catch (e: unknown) {
      // Left visible rather than swallowed: a switch that silently fails
      // leaves you looking at one club believing you are in another.
      setError(e instanceof Error ? e.message : 'Could not switch club')
      setBusy(false)
    }
  }

  const current = orgs.find((o) => o.id === session.org?.id)

  return (
    <div className="org-switcher" ref={box}>
      <button className="org-switcher__button" disabled={busy}
              aria-haspopup="listbox" aria-expanded={open}
              onClick={toggle}>
        {current?.logo_url
          ? <img className="org-switcher__logo" src={current.logo_url} alt="" />
          : <span className="org-switcher__dot" aria-hidden />}
        <span className="org-switcher__name">
          {busy ? 'Switching…' : current?.name ?? 'Choose a club'}
        </span>
        <ChevronsUpDown size={14} aria-hidden />
      </button>

      {error && <div className="org-switcher__error">{error}</div>}

      {open && (
        <ul className="org-switcher__menu" role="listbox"
            style={fixedPos
              ? { position: 'fixed', left: fixedPos.left, top: fixedPos.top, right: 'auto', width: 230 }
              : undefined}>
          {orgs.map((o) => (
            <li key={o.id}>
              <button className="org-switcher__option" role="option"
                      aria-selected={o.id === session.org?.id}
                      onClick={() => void choose(o)}>
                {o.logo_url
                  ? <img className="org-switcher__logo" src={o.logo_url} alt="" />
                  : <span className="org-switcher__dot" aria-hidden />}
                <span className="org-switcher__name">{o.name}</span>
                {o.id === session.org?.id && <Check size={14} aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
